/**
 * Код ANZONIX: полный справочник.
 *
 * Собирается из самого языка: каждая встроенная функция, действие со
 * списком, фигура и каждый индикатор движка со всеми линиями. Добавили
 * функцию — она появилась в справочнике; гадать, как называется команда,
 * не приходится.
 */
import { RULE_INDICATORS } from './indicators.js';
import { BUILTINS, COLORS, DRAW_ALIASES, DRAW_FUNCTIONS, FUNCTION_ALIASES, LIST_FUNCTIONS } from './script.js';
/** Как индикатор называют трейдеры — чтобы поиск находил «свинг», «BOS», «ордерблок». */
const SYNONYMS = {
    swing: 'свинг свинги экстремум вершина впадина pivot high low',
    bos: 'bos пробой структуры break of structure smc',
    choch: 'choch слом характера change of character разворот smc',
    trend_structure: 'тренд структура направление smc',
    sweep: 'ликвидность снятие стопов sweep liquidity grab smc',
    fvg: 'fvg имбаланс разрыв imbalance gap smc',
    order_block: 'ob ордерблок ордер-блок order block smc',
    premium: 'премиум дискаунт equilibrium smc',
    pivots: 'пивот pivot уровни',
    divergence: 'дивергенция расхождение',
    supertrend: 'супертренд',
    ichimoku: 'ишимоку облако',
    bollinger: 'боллинджер полосы',
    vwap: 'vwap средняя цена с объёмом',
};
const CATEGORY_TITLES = {
    trend: 'Индикаторы: тренд',
    momentum: 'Индикаторы: импульс',
    volatility: 'Индикаторы: волатильность',
    volume: 'Индикаторы: объём',
    structure: 'Индикаторы: структура рынка',
    liquidity: 'Индикаторы: ликвидность',
    zone: 'Индикаторы: зоны',
    candle: 'Индикаторы: свечные фигуры',
};
/** Русское имя функции, если оно есть: «ema / эма». */
function aliasOf(canonical, aliases) {
    for (const [alias, target] of Object.entries(aliases))
        if (target === canonical && alias !== canonical && /[а-яё]/.test(alias))
            return alias;
    return null;
}
/** «bos(5, 5).up» — вызов индикатора с настройками по умолчанию. */
export function indicatorCall(spec) {
    const args = spec.params.map((param) => String(param.default)).join(', ');
    const output = spec.outputs && spec.outputs.length > 0 ? `.${spec.outputs[0].key}` : '';
    return `${spec.id}(${args})${output}`;
}
function indicatorHint(spec) {
    const params = spec.params.length ? `настройки: ${spec.params.map((param) => param.label.toLowerCase()).join(', ')}` : 'без настроек';
    const outputs = spec.outputs && spec.outputs.length > 1 ? `; линии: ${spec.outputs.map((output) => `.${output.key} — ${output.label.toLowerCase()}`).join(', ')}` : '';
    const values = spec.values ? `; значения: ${spec.values.map((entry) => `${entry.value} — ${entry.label}`).join(', ')}` : '';
    return `${spec.label}${spec.hint ? `. ${spec.hint}` : ''} (${params}${outputs}${values})`;
}
function builtinItems(names) {
    return names.map((name) => {
        const builtin = BUILTINS[name];
        const alias = aliasOf(name, FUNCTION_ALIASES);
        return [alias ? builtin.usage.replace(name, alias) : builtin.usage, `${builtin.hint}${alias ? ` · по-английски ${name}` : ''}`];
    });
}
export const SCRIPT_REFERENCE = [
    {
        id: 'basics',
        title: 'Как устроен код',
        lead: 'Каждая строка — одно утверждение. Русские слова и английские синонимы равноправны.',
        items: [
            ['настройка период = 14 "Период" от 2 до 100', 'число, которое двигается ползунком (input)'],
            ['имя = ema(закрытие, 20)', 'запомнить величину под именем'],
            ['линия имя "Подпись" мята', 'нарисовать линию (plot)'],
            ['уровень 70 "Перекупленность" красный', 'горизонтальный уровень пунктиром (hline)'],
            ['фон когда условие "Подпись" мята', 'залить фон там, где условие верно (bgcolor)'],
            ['купить когда условие', 'сигнал покупки в момент, когда условие стало верным'],
            ['продать когда условие', 'сигнал продажи'],
            ['сообщить когда условие "Текст"', 'предупреждение без стороны (alert)'],
            ['табло "Тренд" если(т > 0, "вверх", "вниз")', 'строка табло в углу графика — значение на последней свече'],
            ['// комментарий', 'всё после // или # в начале строки — пояснение, код его не читает'],
        ],
    },
    {
        id: 'memory',
        title: 'Память и блоки',
        lead: 'Такой код идёт по свечам слева направо, как вживую: на каждой свече видно только прошлое и её саму.',
        items: [
            ['память тренд = 0', 'величина, которая переходит со свечи на свечу (var); начальное значение — один раз'],
            ['тренд := 1', 'новое значение уже заданного имени'],
            ['счёт += 1', 'прибавить; есть ещё -=  *=  /='],
            ['если условие то\n  …\nиначе если другое\n  …\nиначе\n  …\nконец', 'выполнить по условию; «то» можно не писать'],
            ['если условие то счёт += 1', 'однострочное «если» — без «конец»'],
            ['если условие то x := 1 иначе x := -1', 'однострочное «если … иначе»'],
            ['для i от 0 до 10\n  …\nконец', 'цикл: i = 0, 1, … 10; от большего к меньшему — сам считает вниз'],
            ['для i от 0 до 100 шаг 5\n  …\nконец', 'цикл с шагом'],
            ['пока условие\n  …\nконец', 'повторять, пока условие верно'],
            ['прервать', 'выйти из цикла (break)'],
            ['дальше', 'к следующему повтору цикла (continue)'],
            ['купить', 'внутри блока: событие покупки на этой свече; так же «продать», «сообщить "текст"»'],
            ['закрытие[i]', 'сдвиг, который считается по ходу: в цикле — i свечей назад'],
            ['условие ? да : нет', 'выбор, как в Pine; то же, что если(условие, да, нет)'],
        ],
    },
    {
        id: 'functions',
        title: 'Свои функции',
        lead: 'Функцию задают до того, как вызывают. Сама себя она не вызывает — для повторов есть циклы.',
        items: [
            ['функция размах(а, б) = а - б', 'однострочная функция'],
            ['функция уровень_риска(вход, стоп)\n  риск = модуль(вход - стоп)\n  вернуть риск / вход * 100\nконец', 'функция с телом: «вернуть» отдаёт результат'],
            ['вернуть x', 'отдать результат функции (return)'],
        ],
    },
    {
        id: 'plan',
        title: 'План сделки',
        lead: 'Одна строка «план …» вне блоков. Что не сказано — как в плане по умолчанию; потом план можно поменять во вкладке «Сделка». По плану студия считает статистику, график рисует зоны, бот ведёт позицию.',
        items: [
            ['план стоп свинг 0.3 цели 1 2 3 безубыток 1', 'классика: стоп за свингом с запасом 0,3 ATR, цели 1R/2R/3R, после первой — безубыток'],
            ['план стоп атр 1.5 цели 2', 'стоп в 1,5 среднего хода свечи, одна цель 2R'],
            ['план стоп процент 2 не_дальше 4', 'стоп 2 % от входа, но не дальше 4 ATR'],
            ['план стоп свеча 0.1 цели 2', 'стоп за минимумом свечи сигнала (у продажи — за максимумом) с запасом 0,1 ATR'],
            ['план цели 1 2 3 доли 50 30 20', 'доли позиции на каждой цели, %'],
            ['план трейлинг атр 2', 'стоп тянется за ценой на 2 ATR (после выхода в плюс; «сразу» — с первой свечи)'],
            ['план трейлинг r 1', 'стоп ступенями: каждая пройденная 1R поднимает стоп на 1R'],
            ['план вход откат 50 за 8', 'вход на откате на 50 % пути к стопу, ждать не дольше 8 свечей'],
            ['план время 40', 'закрыть сделку через 40 свечей, если не сработали ни стоп, ни цели'],
            ['план риск 100', 'риск на сделку в долларах — для итога в деньгах'],
        ],
    },
    {
        id: 'lists',
        title: 'Списки',
        lead: 'Список хранит числа и живёт всю историю: уровни, свинги, номера зон.',
        items: [
            ['список уровни', 'завести список — в начале кода, вне блоков'],
            ...Object.entries(LIST_FUNCTIONS).map(([name, spec]) => [spec.usage, `${spec.hint} · ${name}`]),
        ],
    },
    {
        id: 'drawing',
        title: 'Рисование на графике',
        lead: 'Фигура возвращает номер — запомните его, чтобы потом закрыть или удалить. Цвет и подпись — в любом месте после чисел.',
        items: [
            ...Object.entries(DRAW_FUNCTIONS).map(([name, spec]) => [spec.usage, `${spec.hint} · ${Object.entries(DRAW_ALIASES).find(([alias, target]) => target === name && /[a-z]/.test(alias))?.[0] ?? name}`]),
            ['метка(максимум, "BOS", мята)', 'подпись над свечой'],
            ['з := зона(0, максимум, минимум, голубой, "OB")', 'зона от этой свечи; живёт, пока не закроете'],
            ['"Цена " + текст(закрытие, 2)', 'подпись из текста и числа'],
        ],
    },
    {
        id: 'series',
        title: 'Другие таймфреймы и монеты',
        items: [
            ['на("4H", ema(закрытие, 50))', 'посчитать на 4 часах; на своих свечах — закрытый бар'],
            ['на("1д", закрытие > ema(закрытие, 200))', 'условие на дневках: «да» или «нет»'],
            ['монета("ETH", закрытие)', 'цена ETH на тех же свечах'],
            ['монета("BTC/USDT", на("4H", rsi(14)))', 'RSI биткойна на 4 часах — можно вкладывать'],
        ],
    },
    {
        id: 'prices',
        title: 'Цены и время свечи',
        items: [
            ['закрытие', 'close'],
            ['открытие', 'open'],
            ['максимум', 'high'],
            ['минимум', 'low'],
            ['объём', 'volume'],
            ['середина', '(максимум + минимум) / 2 — hl2'],
            ['типичная', '(максимум + минимум + закрытие) / 3 — hlc3'],
            ['закрытие[1]', 'прошлая свеча; [5] — пять назад'],
            ['час', 'час открытия свечи по UTC, 0–23 (hour)'],
            ['минута', 'минута открытия, 0–59 (minute)'],
            ['день_недели', '1 — понедельник … 7 — воскресенье (dayofweek)'],
            ['день', 'число месяца (dayofmonth)'],
            ['месяц', '1–12 (month)'],
            ['год', 'year'],
            ['время', 'время открытия свечи в миллисекундах (time)'],
            ['бар', 'номер свечи с начала загруженной истории, с 0 (bar_index)'],
            ['сессия("08:00-17:00")', 'свеча внутри сессии по UTC; через полночь тоже: "22:00-02:00"'],
        ],
    },
    {
        id: 'averages',
        title: 'Средние, суммы, окна',
        items: builtinItems(['ema', 'sma', 'wma', 'sum', 'highest', 'lowest', 'stdev', 'atr', 'rsi']),
    },
    {
        id: 'vwap',
        title: 'VWAP и периоды',
        lead: 'Средняя цена с учётом объёма с начала периода. Время — UTC, как у криптобирж в TradingView: день с 00:00, неделя с понедельника.',
        items: [
            ...builtinItems(['session_vwap', 'session_vwap_dev', 'anchored_vwap', 'anchored_vwap_dev', 'new_period']),
            ['vwap_сессии(hlc3, "W")', 'VWAP недели'],
            ['vwap_сессии(close, "D") + 2 * vwap_отклонение(close, "D")', 'верхняя полоса дневного VWAP'],
            ['vwap_от(hlc3, bos(5, 5).up)', 'VWAP от последнего пробоя вверх'],
        ],
    },
    {
        id: 'events',
        title: 'Сравнения и события',
        items: [
            ...builtinItems(['crossup', 'crossdown', 'cross', 'rising', 'falling', 'change', 'roc', 'barssince', 'valuewhen', 'pivothigh', 'pivotlow']),
            ['и  или  не', 'and  or  not'],
            ['==  !=  <  >  <=  >=', 'равно, не равно, меньше, больше'],
        ],
    },
    {
        id: 'math',
        title: 'Числа',
        items: [
            ...builtinItems(['abs', 'max', 'min', 'iff', 'nz', 'isna', 'sqrt', 'pow', 'log', 'round', 'floor', 'ceil', 'sign', 'tostr']),
            ['истина  ложь  пусто', 'true, false, na — «пусто» значит «значения нет»'],
        ],
    },
    ...Object.keys(CATEGORY_TITLES)
        .map((category) => ({
        id: `ind-${category}`,
        title: CATEGORY_TITLES[category],
        // ema, rsi и другие с тем же именем, что у встроенной функции, вызываются как встроенные — они в разделах выше.
        items: RULE_INDICATORS.filter((spec) => spec.category === category && !BUILTINS[spec.id]).map((spec) => [indicatorCall(spec), indicatorHint(spec), SYNONYMS[spec.id] ?? '']),
    }))
        .filter((section) => section.items.length > 0),
    {
        id: 'colors',
        title: 'Цвета',
        items: [
            ...['мята', 'зелёный', 'красный', 'фиолетовый', 'голубой', 'синий', 'жёлтый', 'оранжевый', 'розовый', 'белый', 'серый'].map((name) => [name, COLORS[name]]),
            ['#ff5470', 'любой цвет в записи #rrggbb'],
        ],
    },
];
/** Весь справочник одной строкой на запись — для поиска. */
export function searchReference(query) {
    const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return [...SCRIPT_REFERENCE];
    return SCRIPT_REFERENCE.map((section) => ({
        ...section,
        items: section.items.filter(([code, hint, synonyms]) => {
            const text = `${code} ${hint} ${synonyms ?? ''} ${section.title}`.toLowerCase();
            return words.every((word) => text.includes(word));
        }),
    })).filter((section) => section.items.length > 0);
}
//# sourceMappingURL=script-reference.js.map